import { filterMenuItemsBySearch } from './menuUseCase';
import { normalizeUrl } from '../../utils/url';

/**
 * Deepest menu level rendered: L1 sidebar > L2 dropdown > L3 popover group > L4 popover link.
 */
export const MENU_MAX_DEPTH = 4;

/**
 * @typedef {object} MenuLeaf
 * @property {ApiManagerMenuItem} item - Navigable menu item (has a path, no children)
 * @property {string | null} parentLabel - Label of the direct parent (context in flat lists)
 */

/**
 * @typedef {object} MenuPopoverSections
 * @property {ApiManagerMenuItem[]} links - L3 items without children
 * @property {ApiManagerMenuItem[]} groups - L3 items with children, restricted to their non-setting L4
 * @property {ApiManagerMenuItem[]} settingLinks - L3 items flagged `config.setting` without children
 * @property {ApiManagerMenuItem[]} settingGroups - L3 items flagged `config.setting` with children,
 *   and other L3 groups restricted to their L4 flagged `config.setting`
 */

/**
 * @typedef {object} MenuLinkView
 * @property {string} id
 * @property {string} label
 * @property {string} href
 * @property {boolean} isFavorite
 */

/**
 * @typedef {object} MenuLinkGroupView
 * @property {string} id
 * @property {string} title
 * @property {MenuLinkView[]} links
 */

/**
 * @typedef {object} MenuPopoverView
 * @property {MenuLinkView[]} links
 * @property {MenuLinkGroupView[]} groups
 * @property {MenuLinkView[]} settingLinks
 * @property {MenuLinkGroupView[]} settingGroups
 */

/**
 * @param {ApiManagerMenuItem | null | undefined} item
 * @returns {boolean}
 */
export const hasMenuChildren = (item) => Boolean(item?.items?.length);

/**
 * Keep available items only, cut levels deeper than {@link MENU_MAX_DEPTH} and drop
 * non-navigable items (no path) left without visible children.
 * @param {ApiManagerMenuItem[] | null | undefined} items
 * @param {number} [depth=1] - Level of `items` (1 = sidebar)
 * @returns {ApiManagerMenuItem[]}
 */
export const getVisibleMenuItems = (items, depth = 1) => {
  if (!items?.length) return [];

  return items.reduce((visible, item) => {
    if (item.config?.available === false) return visible;

    const children = depth < MENU_MAX_DEPTH ? getVisibleMenuItems(item.items, depth + 1) : [];
    if (!children.length && !item.path) return visible;

    visible.push({ ...item, items: children.length ? children : null });
    return visible;
  }, /** @type {ApiManagerMenuItem[]} */ ([]));
};

/**
 * @param {ApiManagerMenuItem | null | undefined} item
 * @returns {boolean} The user has no access to the item (shown in search only, not navigable)
 */
export const isMenuItemForbidden = (item) => item?.config?.allowed === false;

/**
 * Menu tree searchable by the user: like {@link getVisibleMenuItems} but keeps the items hidden by the user,
 * flagged `config.available = false` along with their descendants, and the forbidden items (`allowed: false`,
 * kept as leaves even without path).
 * @param {ApiManagerMenuItem[] | null | undefined} items
 * @param {number} [depth=1] - Level of `items` (1 = sidebar)
 * @param {boolean} [isParentHidden=false] - An ancestor is hidden
 * @returns {ApiManagerMenuItem[]}
 */
export const getSearchableMenuItems = (items, depth = 1, isParentHidden = false) => {
  if (!items?.length) return [];

  return items.reduce((searchable, item) => {
    const isHidden = isParentHidden || item.config?.available === false;
    const children = depth < MENU_MAX_DEPTH ? getSearchableMenuItems(item.items, depth + 1, isHidden) : [];
    if (!children.length && !item.path && !isMenuItemForbidden(item)) return searchable;

    searchable.push({
      ...item,
      config: { ...item.config, available: !isHidden },
      items: children.length ? children : null,
    });
    return searchable;
  }, /** @type {ApiManagerMenuItem[]} */ ([]));
};

/**
 * Flatten leaves (items without children, with a path or forbidden).
 * @param {ApiManagerMenuItem[] | null | undefined} items
 * @param {string | null} [parentLabel=null]
 * @returns {MenuLeaf[]}
 */
export const getMenuLeaves = (items, parentLabel = null) => {
  if (!items?.length) return [];

  return items.flatMap((item) => {
    if (hasMenuChildren(item)) return getMenuLeaves(item.items, item.label);
    return item.path || isMenuItemForbidden(item) ? [{ item, parentLabel }] : [];
  });
};

/**
 * Favorite leaves of the visible menu.
 * @param {ApiManagerMenuItem[] | null | undefined} visibleItems - Output of {@link getVisibleMenuItems}
 * @returns {MenuLeaf[]}
 */
export const getFavoriteMenuLeaves = (visibleItems) => (
  getMenuLeaves(visibleItems).filter(({ item }) => item.config?.favorite && !isMenuItemForbidden(item))
);

/**
 * Leaves matching a search on their label or an ancestor label.
 * @param {ApiManagerMenuItem[] | null | undefined} searchableItems - Output of {@link getSearchableMenuItems}
 * @param {string} search
 * @returns {MenuLeaf[]}
 */
export const searchMenuLeaves = (searchableItems, search) => {
  if (!search?.trim()) return [];
  return getMenuLeaves(filterMenuItemsBySearch(searchableItems, search));
};

/**
 * Split the children of an L2 item into the popover zones.
 * @param {ApiManagerMenuItem | null | undefined} item - L2 item owning the popover
 * @returns {MenuPopoverSections}
 */
export const getMenuPopoverSections = (item) => {
  /** @type {MenuPopoverSections} */
  const sections = {
    links: [], groups: [], settingLinks: [], settingGroups: [],
  };

  (item?.items ?? []).forEach((child) => {
    const isGroup = hasMenuChildren(child);
    if (child.config?.setting) {
      sections[isGroup ? 'settingGroups' : 'settingLinks'].push(child);
    } else if (isGroup) {
      // L4 flagged `setting` move to a Configuration group titled with their L3 parent
      const settingChildren = child.items.filter((grandChild) => grandChild.config?.setting);
      const regularChildren = child.items.filter((grandChild) => !grandChild.config?.setting);
      if (regularChildren.length) sections.groups.push({ ...child, items: regularChildren });
      if (settingChildren.length) sections.settingGroups.push({ ...child, items: settingChildren });
    } else {
      sections.links.push(child);
    }
  });

  return sections;
};

/**
 * @param {ApiManagerMenuItem[] | null | undefined} items
 * @param {number} levels - Levels left to explore
 * @param {boolean} isHidden - An ancestor is hidden
 * @returns {number}
 */
const countHiddenLeaves = (items, levels, isHidden) => (items ?? []).reduce((count, item) => {
  if (item.config?.allowed === false) return count;
  const hidden = isHidden || item.config?.available === false;
  if (hasMenuChildren(item) && levels > 1) return count + countHiddenLeaves(item.items, levels - 1, hidden);
  return count + (hidden && item.path ? 1 : 0);
}, 0);

/**
 * Number of navigable leaves of a popover hidden by the user (leaf or ancestor unavailable).
 * Forbidden items (`allowed: false`) are not counted.
 * @param {ApiManagerMenuItem | null | undefined} item - Raw item owning the popover (before {@link getVisibleMenuItems})
 * @returns {number}
 */
export const countHiddenMenuLeaves = (item) => countHiddenLeaves(item?.items, MENU_MAX_DEPTH - 2, false);

/**
 * Find a menu item by id at any depth.
 * @param {ApiManagerMenuItem[] | null | undefined} items
 * @param {string | null | undefined} id
 * @returns {ApiManagerMenuItem | null}
 */
export const findMenuItemById = (items, id) => {
  if (!items?.length || !id) return null;

  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    if (item.id === id) return item;
    const found = findMenuItemById(item.items, id);
    if (found) return found;
  }
  return null;
};

/**
 * @param {ApiManagerMenuItem} item
 * @returns {{ id: string, label: string, href: string }}
 */
const toLinkView = (item) => ({ id: item.id, label: item.label, href: normalizeUrl(item.path) ?? '' });

/**
 * @param {ApiManagerMenuItem} item
 * @returns {MenuLinkView}
 */
const toMenuLinkView = (item) => ({
  ...toLinkView(item),
  isFavorite: !!item.config?.favorite,
});

/**
 * @param {ApiManagerMenuItem} item
 * @returns {MenuLinkGroupView}
 */
const toMenuLinkGroupView = (item) => ({
  id: item.id,
  title: item.label,
  links: (item.items ?? []).filter((child) => child.path).map(toMenuLinkView),
});

/**
 * Popover view model of an L2 item (links and groups ready to render).
 * @param {ApiManagerMenuItem | null | undefined} item - L2 item owning the popover
 * @returns {MenuPopoverView}
 */
export const getMenuPopoverView = (item) => {
  const sections = getMenuPopoverSections(item);
  return {
    links: sections.links.map(toMenuLinkView),
    groups: sections.groups.map(toMenuLinkGroupView),
    settingLinks: sections.settingLinks.map(toMenuLinkView),
    settingGroups: sections.settingGroups.map(toMenuLinkGroupView),
  };
};

/**
 * @typedef {object} MenuLeafView
 * @property {string} id
 * @property {string} label
 * @property {string | null} context - Parent label
 * @property {string} href
 * @property {boolean} isHidden - Hidden by the user (search results)
 * @property {boolean} isForbidden - No access for the user (search results): not navigable, empty href
 */

/**
 * Map flat leaves (favorites, search results) to sidebar links.
 * @param {MenuLeaf[]} leaves
 * @returns {MenuLeafView[]}
 */
export const mapMenuLeavesToViews = (leaves) => leaves.map(({ item, parentLabel }) => {
  const isForbidden = isMenuItemForbidden(item);
  return {
    ...toLinkView(item),
    ...(isForbidden ? { href: '' } : {}),
    context: parentLabel,
    isHidden: item.config?.available === false,
    isForbidden,
  };
});
